import Controller from "@ember/controller"
import { action, computed, get, set } from "@ember/object"
import { alias } from "@ember/object/computed"
import { inject as service } from "@ember/service"
import type { Course } from "@shared/models/course"
import type { Planbook } from "@shared/models/planbook"
import type { RotationCalendar } from "@shared/models/rotation-calendar"
import belongsTo from "cc-frontend/helpers/belongs-to"
import findDocument from "cc-frontend/helpers/find-document"
import findMany from "cc-frontend/helpers/find-many"
import hasMany from "cc-frontend/helpers/has-many"
import hasManyCollectively from "cc-frontend/helpers/has-many-collectively"
import preparePlanbookCompleteOnboarding from "cc-frontend/lib/actions/planbook/PLANBOOK_COMPLETE_ONBOARDING"
import type AppState from "cc-frontend/services/app-state"
import type Dispatcher from "cc-frontend/services/dispatcher"
import type EmberStore from "cc-frontend/services/ember-store"
import type ModalManager from "cc-frontend/services/modal-manager"
import type Session from "cc-frontend/services/session"
import { concat, first, includes, last, map, without } from "lodash-es"
import moment from "moment"

export default class PlannerController extends Controller {
  // args
  model!: {
    planbook: Planbook
    rotationCalendar: RotationCalendar
  }
  dateRangeString!: string
  perspective!: string

  // props
  dates!: []
  hiddenCourseIds: Array<string> = []
  classesLinedUp = false
  daysAsRows = false
  lessonsCollapsed = false
  settingsEditorIsVisible: boolean | null = false
  focusedItemId
  selectedCourseId

  date = moment().format("YYYY-MM-DD")

  queryParams = [
    "date",
    "hiddenCourseIds",
    "classesLinedUp",
    "daysAsRows",
    "lessonsCollapsed",
    "focusedItemId",
    "selectedCourseId",
    "stepName",
  ]

  stepName = null

  // Services
  @service dispatcher!: Dispatcher
  @service("ember-store") store!: EmberStore
  @service session!: Session
  @service appState!: AppState
  @service modalManager!: ModalManager
  @service router

  // Computed properties
  @alias("router.currentRouteName") currentPath
  @alias("model.planbook") planbook
  @alias("model.rotationCalendar") rotationCalendar
  @alias("router.currentRoute.queryParams.hideNav") hideNav

  // Models
  @findDocument("selectedCourseId", "course") selectedCourse
  @findMany("visibleCourseIds", "course") visibleCourses
  @hasMany("planbook", "course", "courses") courses
  @hasManyCollectively("planbook", "course", "courses") collectiveCourses
  @belongsTo("planbook", "owner") owner

  @computed("collectiveCourses.isFulfilled", "collectiveCourses.content.[]")
  get instructionalCourses(): Course[] | null {
    if (!this.collectiveCourses.isFulfilled) return null
    return this.collectiveCourses.content.filter((course) => !course.attributes.isNonInstructional)
  }

  @computed("hiddenCourseIds.length")
  get isFilteringClasses() {
    return this.hiddenCourseIds.length > 0
  }

  @computed("isFilteringClasses", "model.planbook.relationships.courses.data.@each.id")
  get visibleCourseIds() {
    return without(
      map(this.model.planbook.relationships.courses.data, (datum) => datum.id),
      ...this.hiddenCourseIds,
    )
  }

  @computed("session.userId", "planbook.attributes.flags.hasNotFinishedOnboarding")
  get shouldBeInOnboarding() {
    // check if they are
    // - if owner
    // - if no classes
    // return FLAG
    return (
      this.session.userId === this.model.planbook.relationships.owner.data.id &&
      this.model.planbook.attributes.flags.isImportingLessons !== true &&
      this.planbook.attributes.flags.hasNotFinishedOnboarding
    )
  }

  @action
  setDate(date) {
    set(this, "date", date)
  }

  @action updateStepName(count) {
    set(this, "stepName", count)
  }

  @action
  printModal(medium) {
    get(this, "dispatcher").dispatch("OPEN_PRINT_MODAL", {
      sessionId: this.appState.id,
      componentId: "print-modal",
      planbookId: this.model.planbook.id,
      startDate: first(this.dates),
      endDate: last(this.dates),
      medium: medium,
      perspective: this.perspective,
      currentUserId: this.session.userId,
      dateRangeString: this.dateRangeString,
    })
  }

  @action
  page(direction) {
    let op = direction === "back" ? "subtract" : "add"
    let date = get(this, "date")
    let newDate
    if (get(this, "perspective") === "day") {
      newDate = moment(date)[op](1, "day")
    } else if (get(this, "perspective") === "week") {
      newDate = moment(date)[op](1, "week")
    } else if (get(this, "perspective") === "month" || get(this, "perspective") === "year") {
      newDate = moment(date)[op](1, "month")
    }
    if (newDate.weekday() === 6) {
      op === "add" ? newDate.add(2, "days") : newDate.subtract(1, "days")
    } else if (newDate.weekday() === 0) {
      op === "add" ? newDate.add(1, "days") : newDate.subtract(2, "days")
    }
    this.set("date", newDate.format("YYYY-MM-DD"))
  }

  @action
  setNewDate(date) {
    set(this, "date", date)
  }

  @action
  async onCourseCreate(courseId: string) {
    const course = await this.store.find("course", courseId)
    this.modalManager.openModal("planner/course/create-edit/create-modal", {
      planbook: this.model.planbook,
      rotationCalendar: this.model.rotationCalendar,
      course,
    })
  }

  @action
  showSettingsEditor() {
    set(this, "settingsEditorIsVisible", true)
  }

  @action
  onSettingsEditorClose() {
    set(this, "settingsEditorIsVisible", false)
  }

  @action
  finishPlanbookOnboarding() {
    this.dispatcher.dispatchPreparedAction(
      preparePlanbookCompleteOnboarding({
        planbookId: this.model.planbook.id,
      }),
    )
  }

  // Show/Hide classes

  @action
  async editCourseTitleAndOrder(courseId: string) {
    const course = await this.store.find("course", courseId)
    this.modalManager.openModal("planner/course/create-edit/edit-modal", {
      planbook: this.model.planbook,
      rotationCalendar: this.model.rotationCalendar,
      course,
    })
  }

  @action
  toggleCourseId(id) {
    // the `set(this,...)` Trigger the change. This will be different once we use @tracked for query params
    // That's pending the resolution of this: https://github.com/emberjs/ember.js/issues/18715
    if (includes(this.hiddenCourseIds, id)) {
      let newIds = without(this.hiddenCourseIds, id)
      set(this, "hiddenCourseIds", newIds)
    } else {
      let newIds = concat(this.hiddenCourseIds, [id])
      set(this, "hiddenCourseIds", newIds)
    }
  }

  @action
  showAllCourses() {
    set(this, "hiddenCourseIds", [])
  }

  @action
  hideAllCourses() {
    let allCourseIds = map(this.model.planbook.relationships.courses.data, (datum) => datum.id)
    set(this, "hiddenCourseIds", allCourseIds)
  }

  // In order / Lined Up
  @action
  showClassesInOrder() {
    // set(this, "classesLinedUp", false)
  }

  @action
  toggleClassesLinedUp() {
    set(this, "classesLinedUp", !this.classesLinedUp)
  }

  // Days lined up
  @action
  showDaysAsRows() {
    set(this, "daysAsRows", !get(this, "daysAsRows"))
  }

  @action
  showDaysAsColumns() {
    set(this, "daysAsRows", false)
  }

  // Lessons Collapsed
  @action
  expandLessons() {
    set(this, "lessonsCollapsed", false)
  }

  @action
  toggleCollapseLessons() {
    set(this, "lessonsCollapsed", !get(this, "lessonsCollapsed"))
  }

  @action
  onSelectCourseId(course) {
    this.set("selectedCourseId", course.id)
  }

  @action
  onNewFocusedItemId(id) {
    this.set("focusedItemId", id)
  }
}
